import { cn } from '@/lib/utils';

const SIZES = {
  md: 'h-8 w-8 text-sm',
  lg: 'h-14 w-14 text-lg',
} as const;

/** Up to two letters off the name — one word gives one, an empty name gives a dash. */
function initialsOf(name: string | null | undefined): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '—';
  return (words[0][0] + (words[1]?.[0] ?? '')).toUpperCase();
}

/**
 * A person, as a photo or as the letters of their name.
 *
 * Two screens had already grown their own fallback and disagreed about both
 * halves of it — one letter against two, `?` against `—` for a member with no
 * name on record. One rule now, because the two sit a click apart.
 *
 * Decorative throughout: `alt=""` on the photo, `aria-hidden` on the letters.
 * The name is always beside it in the same row, and announcing "M" before it
 * only adds a syllable that means nothing.
 */
export function Avatar({
  src,
  name,
  size = 'md',
  className,
}: {
  src?: string | null;
  name?: string | null;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className={cn('shrink-0 rounded-full object-cover', SIZES[size], className)}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        'grid shrink-0 place-items-center rounded-full bg-accent-soft font-semibold text-accent-soft-fg',
        SIZES[size],
        className,
      )}
    >
      {initialsOf(name)}
    </span>
  );
}
